import React, { useEffect, useRef } from 'react';
import ChoicePicker from '../../components/ChoicePicker';
import TextInput from '../../components/TextInput';
import type { RetirementAllowanceFlow } from './useRetirementAllowanceFlow';
import type { RetirementRecipientCategory } from './retirementAllowanceTypes';

const AUTO_INTRO_MS = 550;

const YES_NO = (t: RetirementAllowanceFlow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.dependentDeduction.retirementAllowance.yes') },
  { value: 'no' as const, label: t('submitDocuments.dependentDeduction.retirementAllowance.no') },
];

export default function RetirementAllowanceChatComposer({
  flow,
}: {
  flow: RetirementAllowanceFlow;
}) {
  const tokenRef = useRef(0);

  useEffect(() => {
    if (flow.isLoading || flow.reviewMode) return;
    if (flow.step !== 'intro') return;
    const token = ++tokenRef.current;
    const id = window.setTimeout(() => {
      if (tokenRef.current !== token) return;
      void flow.skipIntro();
    }, AUTO_INTRO_MS);
    return () => window.clearTimeout(id);
  }, [flow]);

  if (flow.reviewMode && flow.step === 'review') return null;
  if (flow.step === 'intro') return null;

  if (flow.step === 'gate') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleGate(v)} />
      </div>
    );
  }

  if (flow.step === 'recipientType') {
    const opts: { value: RetirementRecipientCategory; label: string; disabled?: boolean }[] = [
      {
        value: 'spouse',
        label: flow.t('submitDocuments.dependentDeduction.retirementAllowance.recipient.spouse'),
        disabled: !flow.spouse,
      },
      {
        value: 'dependents',
        label: flow.t('submitDocuments.dependentDeduction.retirementAllowance.recipient.dependents'),
        disabled: flow.dependents.length === 0,
      },
    ].filter((o) => !o.disabled);
    if (opts.length === 0) return null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={opts}
          onChange={(v) => void flow.handleRecipientType(v as RetirementRecipientCategory)}
        />
      </div>
    );
  }

  if (flow.step === 'pickSpouse' && flow.spouse) {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={[
            {
              value: String(flow.spouse.empFamilyId),
              label: `${flow.spouse.fullName} (${flow.t(`submitDocuments.familyInfo.relationship.${flow.spouse.relationship}`)})`,
            },
          ]}
          onChange={() => void flow.handlePickSpouse()}
        />
      </div>
    );
  }

  if (flow.step === 'pickDependent') {
    const opts = flow.availableDependents().map((c) => ({
      value: String(c.empFamilyId),
      label: `${c.fullName} (${flow.t(`submitDocuments.familyInfo.relationship.${c.relationship}`)})`,
    }));
    if (opts.length === 0) return null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={opts}
          onChange={(v) => {
            const c = flow.dependents.find((x) => String(x.empFamilyId) === v);
            if (c) void flow.handlePickDependent(c);
          }}
        />
      </div>
    );
  }

  if (flow.step === 'enterAmount') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <TextInput
          fields={[
            {
              id: 'amount',
              label: flow.t('submitDocuments.dependentDeduction.retirementAllowance.amountLabel'),
              required: true,
            },
          ]}
          values={{ amount: flow.amountDraft }}
          onChange={(_, v) => flow.setAmountDraft(v)}
          onSubmit={() => void flow.handleEnterAmount()}
          submitDisabled={!flow.canSendAmount}
          submitAriaLabel={flow.t('submitDocuments.profileName.send')}
          requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
        />
      </div>
    );
  }

  if (flow.step === 'addAnotherDependent') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={YES_NO(flow.t)}
          onChange={(v) => void flow.handleAddAnotherDependent(v)}
        />
      </div>
    );
  }

  return null;
}
